import React, { useEffect, useState, useRef, Suspense } from 'react';
import './StorePanelBuy.css';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import moment from 'moment-jalaali';
import config from '../../../config';
import Sheet from 'react-modal-sheet';
import api from '../../../hooks/api';
import { useAuthPrompt } from '../../../hooks/auth/useAuthPrompt';

// دسترسی استاندارد به تصاویر پوشه public در ری‌اکت
const zibalIcon = '/images/bankIcons/zibal_icon.png';
const zarinpalIcon = '/images/bankIcons/zarinpal_icon.png';

const toPersianDigits = (num) => {
    if (num === null || num === undefined) return "";
    const persianDigits = "۰۱۲۳۴۵۶۷۸۹";
    return num.toString().replace(/\d/g, (d) => persianDigits[d]);
};

// تعریف درگاه‌های پرداخت: زیبال = 3 و زرین‌پال = 2
const GATEWAYS = [
    {
        id: 'zibal',
        gatewayType: 3, // زیبال = 3
        title: 'درگاه پرداخت زیبال',
        subtitle: 'پرداخت آنلاین با تمامی کارت‌های عضو شتاب',
        icon: zibalIcon,
    },
    {
        id: 'zarinpal',
        gatewayType: 2, // زرین‌پال = 2
        title: 'درگاه پرداخت زرین‌پال',
        subtitle: 'پرداخت امن و سریع اینترنتی',
        icon: zarinpalIcon,
    },
];

function StorePanelBuy({
    isToolbarOpen,
    isLoading,
    availableCredits,
    subscriptionDays,
    subscriptionEndDate,
    chatId,
    onOpenRegister
}) {
    const { triggerRegisterAlert } = useAuthPrompt(onOpenRegister);

    const [isOpen, setOpen] = useState(false);
    const [credit, setCredit] = useState(null);
    const [isRols, setIsRols] = useState(false);
    const [selectedGateway, setSelectedGateway] = useState(3); // مقدار پیش‌فرض: 3 (زیبال)
    const [isBuyClick, setIsBuyClick] = useState(false);
    const [hasReadTerms, setHasReadTerms] = useState(false);

    const termsContainerRef = useRef(null);
    const sheetBodyRef = useRef(null);
    const gatewaySectionRef = useRef(null);

    // محاسبه دینامیک ارتفاع شیت بر حسب صفحه نمایش
    const sheetHeight = typeof window !== 'undefined'
        ? Math.min(650, Math.floor(window.innerHeight * 0.88))
        : 600;

    // ریست کردن وضعیت هنگام بسته شدن شیت
    useEffect(() => {
        if (!isOpen) {
            setIsRols(false);
            setHasReadTerms(false);
            setSelectedGateway(3);
            setIsBuyClick(false);
        }
    }, [isOpen]);

    // بررسی اولیه در صورت کوتاه بودن متن قوانین
    useEffect(() => {
        if (isOpen && termsContainerRef.current) {
            const { scrollHeight, clientHeight } = termsContainerRef.current;
            if (scrollHeight <= clientHeight + 5) {
                setHasReadTerms(true);
            }
        }
    }, [isOpen]);

    // اسکرول نرم به سمت درگاه‌ها به محض تیک زدن قوانین
    useEffect(() => {
        if (isRols && gatewaySectionRef.current) {
            const timer = setTimeout(() => {
                gatewaySectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isRols]);

    const handleScrollTerms = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight - 8) {
            setHasReadTerms(true);
        }
    };

    const handleRulesChange = (e) => {
        const checked = e.target.checked;
        setIsRols(checked);
        if (checked && !selectedGateway) {
            setSelectedGateway(3);
        }
    };

    const handleSelectGateway = (gwType) => {
        if (!isRols) return;
        setSelectedGateway(gwType);
    };

    // فراخوانی ماژول مشترک عضویت
    const showRegisterAlert = () => {
        triggerRegisterAlert({
            onBeforeOpen: () => setOpen(false),
            delay: 150
        });
    };

    const saveEvent = (e) => {
        e.preventDefault();
        if (isBuyClick) return;

        if (!credit) {
            toast.error("لطفاً ابتدا یک بسته را انتخاب کنید.", { rtl: true });
            return;
        }

        if (!isRols) {
            toast.error("لطفاً قوانین و شرایط را بپذیرید.", { rtl: true });
            return;
        }

        if (!selectedGateway) {
            toast.error("لطفاً درگاه پرداخت را انتخاب کنید.", { rtl: true });
            return;
        }

        setIsBuyClick(true);

        const data = {
            credit_id: credit.id,
            isAcceptRules: isRols,
            gatewayType: Number(selectedGateway)
        };

        api.post(`business/store/credit`, data)
            .then((response) => {
                if (response.data?.success) {
                    const paymentUrl = response.data.data;
                    window.location.href = paymentUrl;
                } else {
                    setIsBuyClick(false);
                    const msg = response.data?.message || "";
                    if (msg.includes("عضو") || msg.includes("ربات")) {
                        showRegisterAlert();
                    } else {
                        toast.error(msg || "خطایی رخ داده است", { rtl: true });
                    }
                }
            })
            .catch((error) => {
                setIsBuyClick(false);
                const errorMsg = error.response?.data?.message || "";
                const status = error.response?.status;

                if (status === 404 || errorMsg.includes("عضو") || errorMsg.includes("ربات")) {
                    showRegisterAlert();
                } else {
                    toast.error(errorMsg || "خطا در برقراری ارتباط با سرور", { rtl: true });
                }
            });
    };

    return (
        <>
            <div className={`store-panel-buy ${isToolbarOpen ? 'with-toolbar-spacing' : ''}`}>
                {isLoading ? (
                    <div className="store-panel-buy-cards" style={{ paddingTop: '20px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '30px', marginTop: '10px' }}>
                            <div className="sp-skeleton-shimmer" style={{ width: '110px', height: '28px', borderRadius: '15px', marginBottom: '15px' }}></div>
                            <div className="sp-skeleton-shimmer" style={{ width: '190px', height: '40px', borderRadius: '8px', marginBottom: '10px' }}></div>
                            <div className="sp-skeleton-shimmer" style={{ width: '130px', height: '16px', borderRadius: '4px' }}></div>
                        </div>

                        {[1, 2, 3].map((item) => (
                            <div className="store-panel-buy-wrapper" key={`skeleton-${item}`}>
                                <div className='store-panel-buy-card'>
                                    <div className='logo sp-skeleton-shimmer'></div>
                                    <div className="title sp-skeleton-shimmer" style={{ width: '60%', height: '16px', borderRadius: '4px' }}></div>
                                    <div className="description sp-skeleton-shimmer" style={{ width: '90%', height: '12px', borderRadius: '4px', marginTop: '4px' }}></div>
                                    <div className="extra-description sp-skeleton-shimmer" style={{ width: '40%', height: '10px', borderRadius: '4px', marginTop: '4px' }}></div>
                                    <div className="price sp-skeleton-shimmer" style={{ width: '80px', height: '22px', borderRadius: '15px' }}></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <StatusSection days={subscriptionDays} endDate={subscriptionEndDate} />

                        <div className="store-panel-buy-cards">
                            {availableCredits?.map((item) => (
                                <div className="store-panel-buy-wrapper" key={item.id}>
                                    <div className='store-panel-buy-card' onClick={(e) => {
                                        setCredit(item);
                                        setOpen(true);
                                        e.stopPropagation();
                                    }}>
                                        <div className='logo'>
                                            <img src={`${config.api.imageURL}/${item.image_path}`} alt={item.title} loading="lazy" />
                                        </div>
                                        <div className="title">{item.title}</div>
                                        <div className="description">{item.description}</div>
                                        <div className="extra-description">{item.extra_description}</div>
                                        <div className="price">{toPersianDigits(Math.round(item.price).toLocaleString())} تومان</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <Suspense fallback={null}>
                {isOpen && (
                    <Sheet
                        isOpen={isOpen}
                        onClose={() => setOpen(false)}
                        snapPoints={[sheetHeight, 0]}
                        springConfig={{ stiffness: 300, damping: 30 }}
                    >
                        <Sheet.Container>
                            <Sheet.Header />
                            <Sheet.Content>
                                <div className='store-panel-buy-content'>
                                    <div className="store-panel-buy-content-info">
                                        {credit && <div className='title'>خرید طرح: {credit.title}</div>}
                                    </div>

                                    <div className="store-panel-buy-body" ref={sheetBodyRef}>
                                        <div className="store-panel-buy-content-law">
                                            {!hasReadTerms && (
                                                <div className="terms-scroll-hint">
                                                    لطفاً متن قوانین را تا انتها اسکرول کنید &darr;
                                                </div>
                                            )}

                                            <TermsText ref={termsContainerRef} onScroll={handleScrollTerms} />

                                            <label className={`rols ${!hasReadTerms ? 'disabled' : ''} ${isRols ? 'checked' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={isRols}
                                                    onChange={handleRulesChange}
                                                    disabled={!hasReadTerms}
                                                />
                                                <span>تمامی قوانین و شرایط فوق را مطالعه کرده و می‌پذیرم.</span>
                                            </label>
                                        </div>

                                        <div
                                            className={`store-panel-gateways-section ${!isRols ? 'section-disabled' : ''}`}
                                            ref={gatewaySectionRef}
                                        >
                                            <div className="gateway-section-title">انتخاب درگاه پرداخت</div>
                                            <div className="gateway-list">
                                                {GATEWAYS.map((gw) => {
                                                    const isSelected = selectedGateway === gw.gatewayType;
                                                    return (
                                                        <div
                                                            key={gw.id}
                                                            className={`gateway-item ${isSelected ? 'selected' : ''} ${!isRols ? 'disabled' : ''}`}
                                                            onClick={() => handleSelectGateway(gw.gatewayType)}
                                                        >
                                                            <div className="gateway-item-right">
                                                                <div className="gateway-logo-box">
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL || ''}${gw.icon}`}
                                                                        alt={gw.title}
                                                                        loading="lazy"
                                                                    />
                                                                </div>
                                                                <div className="gateway-text-info">
                                                                    <span className="gateway-name">{gw.title}</span>
                                                                    <span className="gateway-desc">{gw.subtitle}</span>
                                                                </div>
                                                            </div>
                                                            <div className="gateway-radio">
                                                                <span className="radio-outer">
                                                                    {isSelected && <span className="radio-inner" />}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="store-panel-buy-content-pay">
                                            <button
                                                disabled={!isRols || !hasReadTerms || !selectedGateway || isBuyClick}
                                                onClick={saveEvent}
                                                className={(!isRols || !hasReadTerms || !selectedGateway) ? 'btn-disabled' : ''}
                                            >
                                                {isBuyClick ? (
                                                    <div className="btn-loading-wrapper">
                                                        <ReactLoading type={"bubbles"} color={"#ffffff"} height={25} width={40} />
                                                    </div>
                                                ) : (
                                                    credit
                                                        ? `پرداخت و فعال‌سازی (${toPersianDigits(Math.round(credit.price).toLocaleString())} تومان)`
                                                        : "پرداخت"
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Sheet.Content>
                        </Sheet.Container>
                        <Sheet.Backdrop onClick={() => setOpen(false)} />
                    </Sheet>
                )}
            </Suspense>
        </>
    );
}

// --- کامپوننت‌های کمکی ---

const StatusSection = ({ days, endDate }) => {
    if (!days && !endDate?.date) {
        return null;
    }

    return (
        <div className="store-panel-buy-sharing">
            {days === 0 && endDate?.date && (
                <div className="credit-sharing-end-point">
                    <div className='info'>اشتراک به پایان رسیده</div>
                    <div className="date">
                        <span>انقضا: </span>
                        <span>{toPersianDigits(moment(endDate.date).format('jYYYY/jMM/jDD'))}</span>
                    </div>
                </div>
            )}

            {days > 0 && (
                <div className="credit-sharing-active">
                    <div className="status-header">
                        <div className="pulse-dot"></div>
                        <div className="title">اشتراک فعال</div>
                    </div>
                    <div className="info-display">
                        <div className="big-number">{toPersianDigits(days)}</div>
                        <div className="sub-label">روز باقی‌مانده</div>
                    </div>
                    <div className="expiry-date">
                        {`انقضا: ${endDate?.date ? toPersianDigits(moment(endDate.date).format('jYYYY/jMM/jDD')) : ''}`}
                    </div>
                </div>
            )}
        </div>
    );
};

const TermsText = React.forwardRef(({ onScroll }, ref) => (
    <div className="context" ref={ref} onScroll={onScroll}>
        <h3>قوانین و شرایط استفاده از خدمات پلتفرم</h3>
        <p>کاربر گرامی، ضمن سپاس از انتخاب شما، خواهشمند است موارد زیر را جهت استفاده بهینه از خدمات پلتفرم به دقت مطالعه فرمایید. خرید اشتراک و استفاده از خدمات به منزله پذیرش کامل قوانین زیر است:</p>
        <h4>۱. تعاریف و کلیات</h4>
        <p>این پلتفرم ارائه‌دهنده زیرساخت فنی جهت ایجاد فروشگاه آنلاین در بستر تلگرام (مینی‌اپ) و وب می‌باشد. هر فروشگاه ایجاد شده دارای هویت مستقل بوده و مالک فروشگاه مسئول تمامی فعالیت‌های تجاری آن است.</p>
        <h4>۲. استقلال فروشگاه‌ها</h4>
        <p>هر فروشگاه دارای درگاه پرداخت مستقل، شماره کارت اختصاصی و سیستم صدور فاکتور جداگانه است. پلتفرم هیچ‌گونه دخل و تصرفی در وجوه دریافتی فروشندگان نداشته و صرفاً بستر فنی را فراهم می‌کند. تمامی تراکنش‌ها مستقیماً بین خریدار و فروشگاه انجام می‌شود.</p>
        <h4>۳. شرایط خرید اشتراک</h4>
        <p>اشتراک‌های خریداری شده (طرح‌ها) جهت فعال‌سازی امکانات فنی فروشگاه شما می‌باشد. با توجه به ماهیت دیجیتال خدمات، پس از پرداخت و فعال‌سازی آنی پنل، امکان لغو و استرداد وجه وجود ندارد. لطفاً در انتخاب طرح دقت فرمایید.</p>
        <h4>۴. مسئولیت محتوا و کالا</h4>
        <p>فروش و عرضه هرگونه کالا یا خدمات مغایر با قوانین جمهوری اسلامی ایران، کالاهای قاچاق، ممنوعه و غیراخلاقی در این بستر ممنوع است. مسئولیت حقوقی و کیفری ناشی از محتوای فروشگاه و کالاهای ارائه شده، تماماً بر عهده مالک فروشگاه می‌باشد.</p>
        <h4>۵. لینک‌های خارجی (اینستاگرام)</h4>
        <p>قابلیت تبدیل لینک‌های فروشگاه به لینک مستقیم جهت استفاده در اینستاگرام و سایر شبکه‌های اجتماعی فراهم شده است. عملکرد صحیح این لینک‌ها تابع قوانین پلتفرم‌های مقصد بوده و مسئولیت نحوه انتشار آن با کاربر است.</p>
        <h4>۶. پشتیبانی و پایداری</h4>
        <p>تیم فنی ما متعهد به ارائه بالاترین سطح پایداری و امنیت سرورها می‌باشد. با این حال، اختلالات ناشی از زیرساخت اینترنت سراسری یا اختلالات خود پلتفرم تلگرام، خارج از کنترل ما بوده و مسئولیتی متوجه پلتفرم نخواهد بود.</p>
        <p className="final-note">با تأیید این قرارداد، شما اذعان می‌دارید که تمامی موارد فوق را مطالعه کرده و به آن پایبند هستید.</p>
    </div>
));

export default StorePanelBuy;
